import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize } from 'rxjs';
import {
  PlanoAlteracao,
  PlanoCadastro,
  PlanoDetalhe,
  PlanoResumo,
  ProdutoPlano,
} from '../../../core/models/parametros/plano.model';
import { CodigoPlanoEmUsoError, PlanosService } from '../../../core/services/parametros/planos.service';
import { ToastService } from '../../../core/services/toast.service';
import { opcaoProduto, produtoDaOpcao, produtosLivres, rotuloProduto } from '../../../core/utils/parametros/planos.util';
import { PlanoCabecalhoComponent } from '../../../shared/components/parametros/plano-cabecalho/plano-cabecalho.component';
import { TrilhaNavegacaoComponent } from '../../../shared/components/trilha-navegacao/trilha-navegacao.component';
import { PlanoInstitutosComponent } from './plano-institutos/plano-institutos.component';
import { BeneficiosComponent } from '../beneficios/beneficios.component';
import { RequerimentoOnlineComponent } from '../requerimento-online/requerimento-online.component';
import { VivestInputValueAccessorDirective } from '../../../shared/directives/input-vivest/vivest-input-value-accessor.directive';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestRadioValueAccessorDirective } from '../../../shared/directives/radio-vivest/vivest-radio-value-accessor.directive';
import { VivestListaVisivelDirective } from '../../../shared/directives/select-vivest/vivest-lista-visivel.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';

export const CODIGO_PLANO_MAXIMO = 20;
export const NOME_PLANO_MAXIMO = 100;
export const DESCRICAO_PLANO_MAXIMO = 1000;

export type AbaPlano = 'dados' | 'instituto' | 'beneficios' | 'requerimento' | 'documentos' | 'outras';

/** Abas da parametrização do plano. */
export const ABAS_PLANO: readonly { readonly id: AbaPlano; readonly rotulo: string }[] = [
  { id: 'dados', rotulo: 'Dados do Plano' },
  { id: 'instituto', rotulo: 'Instituto' },
  { id: 'beneficios', rotulo: 'Benefícios' },
  { id: 'requerimento', rotulo: 'Requerimento Online' },
  { id: 'documentos', rotulo: 'Documentos' },
  { id: 'outras', rotulo: 'Outras Configurações' },
];

/** Abas que só ficam habilitadas na edição do plano. */
const ABAS_EDICAO: ReadonlySet<AbaPlano> = new Set<AbaPlano>(['instituto', 'beneficios', 'requerimento']);

/**
 * Parametrização do plano, nas rotas /planos/novo ("Novo Plano") e /planos/:id/editar
 * ("Editar" no menu da listagem). As duas usam a mesma aba "Dados do Plano":
 * - no cadastro, o código do plano é escolhido na lista de planos vigentes (é o produto), o nome é
 *   sugerido a partir dele e só essa aba fica habilitada;
 * - na edição, o cabeçalho mostra o plano, código e produto não mudam e a aba "Instituto" também
 *   fica habilitada, com os institutos do plano (`PlanoInstitutosComponent`).
 * Salvar pede confirmação, grava pela API e volta para a listagem.
 */
@Component({
  selector: 'app-plano-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TrilhaNavegacaoComponent,
    PlanoCabecalhoComponent,
    PlanoInstitutosComponent,
    BeneficiosComponent,
    RequerimentoOnlineComponent,
    VivestInputValueAccessorDirective,
    VivestRadioValueAccessorDirective,
    VivestListaVisivelDirective,
    VivestModalAreaVisivelDirective,
  ],
  // Web components do design system Vivest (autocomplete, input, radio, button, icon, card, modal).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './plano-cadastro.component.html',
  styleUrl: './plano-cadastro.component.scss',
})
export class PlanoCadastroComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly planosService = inject(PlanosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Id do plano em edição (rota /planos/:id/editar); `null` no cadastro. */
  private readonly rota = inject(ActivatedRoute).snapshot;
  readonly idEdicao = idDaRota(this.rota.paramMap.get('id'));
  readonly editando = this.idEdicao !== null;

  readonly trilha = ['Planos', 'Parâmetros de Plano', 'Novo Plano'];
  readonly abas = ABAS_PLANO;
  readonly abaAtiva = signal<AbaPlano>('dados');
  /** A aba "Instituto" só carrega quando é aberta pela primeira vez. */
  readonly institutoAberto = signal(false);
  /** A aba "Benefícios" também só carrega na primeira visita. */
  readonly beneficiosAberto = signal(false);
  /** A aba "Requerimento Online" também só carrega na primeira visita. */
  readonly requerimentoAberto = signal(false);
  readonly codigoMaximo = CODIGO_PLANO_MAXIMO;
  readonly descricaoMaximo = DESCRICAO_PLANO_MAXIMO;

  /** Plano carregado na edição, exibido no cabeçalho. */
  readonly plano = signal<PlanoDetalhe | null>(null);

  readonly salvando = signal(false);
  readonly confirmandoSalvar = signal(false);
  /** Depois da primeira tentativa de salvar, todos os erros ficam visíveis. */
  readonly tentouSalvar = signal(false);

  /** Lista do campo "Código do Plano" do cadastro (planos vigentes de /v1/planos). */
  readonly produtos = signal<ProdutoPlano[]>([]);
  readonly carregandoProdutos = signal(true);
  /** Uma parametrização por produto: só entram os produtos com código no Amadeus e ainda sem parametrização. */
  readonly opcoesProduto = computed(() =>
    produtosLivres(this.produtos()).map(opcaoProduto),
  );

  /**
   * Na edição todos os campos são obrigatórios e o status pode mudar (no cadastro todo plano nasce
   * Ativo). No cadastro só há "Código do Plano" (o produto, escolhido na lista; texto digitado não
   * vale) e "Nome do Plano": código digitado e descrição ficam desabilitados e não são gravados.
   * O tipo do plano não é informado nem no cadastro nem na edição.
   */
  readonly form = this.fb.nonNullable.group({
    codigo: ['', [Validators.required, Validators.maxLength(CODIGO_PLANO_MAXIMO)]],
    produto: [null as ProdutoPlano | null, Validators.required],
    nome: ['', [Validators.required, Validators.maxLength(NOME_PLANO_MAXIMO)]],
    descricao: ['', [Validators.required, Validators.maxLength(DESCRICAO_PLANO_MAXIMO)]],
    status: ['Ativo' as PlanoResumo['status'], Validators.required],
  });

  private readonly statusFormulario = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  private readonly valoresFormulario = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });

  /**
   * "Salvar" só habilita com todos os campos obrigatórios preenchidos (e válidos). Na edição,
   * também precisa haver alguma alteração.
   */
  readonly podeSalvar = computed(() => {
    this.valoresFormulario();
    const valido = this.statusFormulario() === 'VALID';
    return valido && !this.salvando() && (!this.editando || this.form.dirty);
  });

  readonly tituloConfirmacao = this.editando ? 'Salvar alterações' : 'Cadastrar plano';

  /** Texto do modal de confirmação, com o plano que será gravado. */
  readonly descricaoConfirmacao = computed(() => {
    this.valoresFormulario();
    const { codigo, produto, nome } = this.form.getRawValue();
    const plano = `${this.editando ? codigo.trim() : (produto?.codigo ?? '')} - ${nome.trim()}`;
    return this.editando ? `Confirma as alterações do plano ${plano}?` : `Confirma o cadastro do plano ${plano}?`;
  });

  constructor() {
    // O loader só some com os dados carregados e o formulário desenhado.
    this.loaderService.showLoader();
    if (this.idEdicao !== null) {
      this.carregarPlano(this.idEdicao);
      // ?aba=instituto: retorno de uma tela aberta pela aba "Instituto" (ex.: extrato de desligamento).
      if (this.rota.queryParamMap.get('aba') === 'instituto') {
        this.selecionarAba('instituto');
      }
    } else {
      this.form.controls.codigo.disable();
      this.form.controls.descricao.disable();
      this.carregarProdutos();
    }
  }

  // ── abas ────────────────────────────────────────────────────────────────────

  /**
   * "Dados do Plano" sempre; "Instituto", "Benefícios" e "Requerimento Online" só na edição; as
   * demais ainda não existem.
   */
  abaHabilitada(aba: AbaPlano): boolean {
    return aba === 'dados' || (ABAS_EDICAO.has(aba) && this.editando);
  }

  selecionarAba(aba: AbaPlano): void {
    if (this.abaHabilitada(aba)) {
      this.abaAtiva.set(aba);
      if (aba === 'instituto') {
        this.institutoAberto.set(true);
      } else if (aba === 'beneficios') {
        this.beneficiosAberto.set(true);
      } else if (aba === 'requerimento') {
        this.requerimentoAberto.set(true);
      }
    }
  }

  // ── código do plano / produto (cadastro) ────────────────────────────────────

  /** Plano escolhido na lista: grava o produto e sugere o nome, se ainda estiver vazio. */
  selecionarProduto(event: Event): void {
    const opcao = (event as CustomEvent<{ value: string }>).detail;
    const produto = produtoDaOpcao(this.produtos(), opcao?.value);
    if (!produto) {
      return;
    }
    this.form.controls.produto.markAsTouched();
    this.form.controls.produto.setValue(produto);
    if (!this.form.controls.nome.value.trim()) {
      this.form.controls.nome.setValue(produto.descricao);
    }
  }

  /** Digitar no campo desfaz a escolha anterior até um produto da lista ser escolhido de novo. */
  digitarProduto(event: Event): void {
    const texto = (event.target as HTMLElement & { value: string }).value;
    const produto = this.form.controls.produto.value;
    if (!produto || texto !== rotuloProduto(produto)) {
      this.form.controls.produto.setValue(null);
    }
  }

  aoSairDoProduto(): void {
    this.form.controls.produto.markAsTouched();
  }

  /** Mensagem de erro do campo, exibida depois que ele é tocado ou ao tentar salvar. */
  erro(campo: 'codigo' | 'produto' | 'nome' | 'descricao'): string {
    const controle = this.form.controls[campo];
    if (controle.valid || !(controle.touched || this.tentouSalvar())) {
      return '';
    }
    if (controle.hasError('codigoEmUso')) {
      return 'Este plano já está parametrizado.';
    }
    const maximo = controle.getError('maxlength')?.requiredLength;
    if (maximo) {
      return `Use no máximo ${maximo} caracteres.`;
    }
    const mensagens = {
      codigo: 'Informe o código do plano.',
      produto: 'Selecione o código do plano na lista.',
      nome: 'Informe o nome do plano.',
      descricao: 'Informe a descrição do plano.',
    };
    return mensagens[campo];
  }

  // ── salvar ──────────────────────────────────────────────────────────────────

  /** "Salvar" só pede confirmação; a gravação acontece em `confirmarSalvar`. */
  salvar(): void {
    this.tentouSalvar.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid || this.salvando()) {
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.form.invalid || this.salvando()) {
      return;
    }

    const { produto, nome, descricao, status } = this.form.getRawValue();
    const alteracao: PlanoAlteracao = { nome, descricao, status };
    const cadastro: PlanoCadastro = { produto: produto as ProdutoPlano, nome, status };
    const gravacao: Observable<PlanoDetalhe> =
      this.idEdicao !== null
        ? this.planosService.atualizarPlano(this.idEdicao, alteracao)
        : this.planosService.criarPlano(cadastro);

    this.salvando.set(true);
    this.loaderService.showLoader();
    gravacao
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (plano) => {
          this.toastService.show(`Plano ${plano.nome} ${this.editando ? 'atualizado' : 'cadastrado'}.`, 'success');
          void this.router.navigate(['/planos']);
        },
        error: (erro: unknown) => {
          if (erro instanceof CodigoPlanoEmUsoError) {
            // No cadastro, o código do plano é o produto escolhido na lista.
            this.form.controls.produto.setErrors({ codigoEmUso: true });
            return;
          }
          this.toastService.show(
            this.editando ? 'Não foi possível salvar as alterações do plano.' : 'Não foi possível cadastrar o plano.',
            'error',
          );
        },
      });
  }

  voltarParaLista(): void {
    void this.router.navigate(['/planos']);
  }

  // ── carregamento ────────────────────────────────────────────────────────────

  private carregarProdutos(): void {
    this.planosService
      .listarProdutos()
      .pipe(
        finalize(() => this.carregandoProdutos.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (produtos) => {
          this.produtos.set(produtos);
          this.esconderLoaderAposRender();
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a lista de produtos.', 'error');
        },
      });
  }

  /** Edição: preenche o formulário com o plano; id inexistente volta para a listagem. */
  private carregarPlano(id: number): void {
    this.planosService
      .obterPlano(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (plano) => {
          this.plano.set(plano);
          this.form.reset({
            codigo: plano.codigo,
            produto: plano.produto,
            nome: plano.nome,
            descricao: plano.descricao,
            status: plano.status,
          });
          this.esconderLoaderAposRender();
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Plano não encontrado.', 'error');
          void this.router.navigate(['/planos']);
        },
      });
  }

  private esconderLoaderAposRender(): void {
    afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
  }
}

/** Id numérico da rota de edição; ausente ou inválido vira `null` (cadastro). */
function idDaRota(valor: string | null): number | null {
  return valor !== null && /^\d+$/.test(valor) ? Number(valor) : null;
}
